import { delay } from '@common/onboarding/dom-util';
import { captureException } from '@common/sentry';
import { SentryBreadcrumb, SentryBreadcrumbType } from '@common/sentry/SentryBreadcrumb';
import { GqlErrorTags, SentryTags, ErrorTags, HTTPErrorTags } from '@common/sentry/SentryTags';
import { mdClient } from '@LSP/websocket/MdClient/MdWebSocketBase';
import { AsyncEventProgress } from '@LSP/websocket/MdClient/queries';
import { Callable, NestedData } from '@prophecy/interfaces/generic';
import { message } from '@prophecy/ui-v3/ui-compat';
import { FROZEN_EMPTY_ARRAY } from '@prophecy/utils/constants';
import { isNodeEvnProduction } from '@prophecy/utils/env';
import { ProphecyError, isProphecyError } from '@prophecy/utils/error';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { getTabId } from '@prophecy/utils/session';
import { addBreadcrumb } from '@sentry/react';
import { partialMatchKey } from '@tanstack/query-core/utils';
import {
  MutationFunction,
  QueryKey,
  useMutation as useReactMutation,
  UseMutationOptions,
  useQuery as useReactQuery,
  useInfiniteQuery as useReactInfiniteQuery,
  UseQueryOptions,
  UseInfiniteQueryOptions,
  useMutation,
  useIsFetching,
  QueryFunction,
  useQueryClient
} from '@tanstack/react-query';
import { TadaDocumentNode } from 'gql.tada';
import { GraphQLClient, RequestDocument, Variables } from 'graphql-request';
import { noop, isString } from 'lodash-es';
import { useState, useEffect, useCallback, useRef } from 'react';

import { csrf } from '../utils/csrf';
import { getGraphQlEndPoint, getGraphQlEndPointNew } from '../utils/getServerUrl';
import { replayToken } from '../utils/replayToken';
import { shareToken } from '../utils/shareToken';
import { TAB_ID_HEADER } from '../utils/tabId';
import { DEFAULT_QUERY_NOTIFY_PROPS } from './queryConstants';
import { authorizedFetch, authorizedFetchWithoutShareTokens, handleUnAuthorizedCall } from './rest/autorizedFetch';
import { makeRawRestCall, makeRestCall, makeRestCallWithoutShareTokens } from './rest/makeRestCall';

const endpoint = getGraphQlEndPoint();
const endpointNew = getGraphQlEndPointNew();

export type ExecutionServiceError = { msg: string; message: string; trace: string[] | string };

export type ReactQueryError = { errors: { message: string }[] };

export type Headers = {
  [key: string]: string;
};

function getQueryKeyWithVariables(key: QueryKey, variables?: unknown) {
  if (variables !== undefined) {
    return [...key, variables];
  }

  return key;
}

const formatErrorTrace = (trace?: string | string[]) => {
  return Array.isArray(trace) ? trace.join('\n') : trace;
};

type ErrorHandlerOptions = {
  sentryTag?: string;
  showErrorToast?: boolean;
};

export function errorHandler(exception: unknown): void;
export function errorHandler(exception: unknown, options?: ErrorHandlerOptions): void;
export function errorHandler(
  exception: unknown,
  { sentryTag, showErrorToast }: ErrorHandlerOptions = { showErrorToast: true }
) {
  let error = exception as ProphecyError;
  if (!sentryTag && !isNodeEvnProduction()) {
    console.warn('sentryTag is mandatory when capturing exception');
  }

  if (!isProphecyError(error)) {
    error = convertToProphecyError(exception);
  }

  captureException({
    exception: error,
    errorTags: { [SentryTags.ProphecyErrorHttpType]: sentryTag || HTTPErrorTags.QueryError }
  });

  // If the error is not thrown by us, mask stackTrace and message for user
  if (!error.cause) {
    console.error(error);
    error = new ProphecyError('Something went wrong');
  }

  const shouldShowErrorToast = showErrorToast ?? true;
  if (shouldShowErrorToast) {
    message.error({ content: error.message, detail: error.stack });
  }
}
function isExecutionServiceError(exception: unknown): exception is ExecutionServiceError {
  return Boolean((exception as ExecutionServiceError)?.message || (exception as ExecutionServiceError)?.msg);
}
function convertToProphecyError(exception: unknown) {
  const message = (exception as ExecutionServiceError)?.message || (exception as ExecutionServiceError)?.msg;
  if (isExecutionServiceError(exception)) {
    const err = exception;
    const error = new ProphecyError(message);
    error.stack = formatErrorTrace(err?.trace);
    return error;
  } else if (isString(exception)) {
    return new ProphecyError(exception);
  } else {
    return new ProphecyError('Something went wrong');
  }
}

export const uploadFiles = async ({
  url,
  method,
  body,
  headers,
  onProgress: callBack,
  signal
}: {
  url: string;
  method: string;
  body: XMLHttpRequestBodyInit;
  headers?: Headers;
  onProgress: (percentage: number, error?: boolean) => void;
  signal?: AbortSignal;
}) => {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new DOMException('Upload aborted', 'AbortError'));
    }
    const shareTokenValue = shareToken.get();
    const replayTokenValue = replayToken.get();
    const xhr = new XMLHttpRequest();
    let progress = 0;
    let aborted = false;
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          aborted = true;
          xhr.abort();
        },
        { once: true }
      );
    }
    xhr.open(method, url, true);
    if (headers) {
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });
    }
    xhr.setRequestHeader(csrf.key, csrf.get());
    xhr.setRequestHeader(TAB_ID_HEADER, getTabId());
    // oxlint-disable-next-line no-unused-expressions
    shareTokenValue && xhr.setRequestHeader(shareToken.key, shareTokenValue);
    // oxlint-disable-next-line no-unused-expressions
    replayTokenValue && xhr.setRequestHeader(replayToken.key, replayTokenValue);
    xhr.withCredentials = true;
    xhr.setRequestHeader('accept', 'application/json;');
    xhr.addEventListener('loadend', async () => {
      if (aborted) {
        return reject(new DOMException('Upload aborted', 'AbortError'));
      }
      const isAuthorized = handleUnAuthorizedCall(xhr.status, url);
      if (!isAuthorized) {
        return reject(xhr.response);
      }
      if (xhr.status !== 200) {
        callBack(100, true);
        reject(new Error(xhr.response));
      } else {
        const response = new Response(xhr.responseText, {
          status: xhr.status,
          statusText: xhr.statusText
        });
        try {
          const resp = await response.json();
          if (progress !== 100) {
            callBack(100);
          }
          resolve(resp);
        } catch (e) {
          callBack(100, true);
          captureException({
            exception: e,
            errorTags: { [SentryTags.ProphecyErrorType]: ErrorTags.JavascriptError }
          });
          throw new ProphecyError((e as { message: string })?.message || 'Something went wrong in an API');
        }
      }
    });
    xhr.upload.addEventListener('progress', (event) => {
      progress = (event.loaded / event.total) * 100;
      callBack(progress);
    });
    xhr.send(body as XMLHttpRequestBodyInit);
    xhr.onerror = reject;
  });
};

const graphQLClient = new GraphQLClient(endpoint, {
  fetch: authorizedFetch
});

const graphQLClientNew = new GraphQLClient(endpointNew, {
  fetch: authorizedFetch
});

const graphQLClientWithoutShareTokens = new GraphQLClient(endpoint, {
  fetch: authorizedFetchWithoutShareTokens
});

const graphQlBeaconClient = new GraphQLClient(endpoint, {
  fetch: (url: URL | RequestInfo, requestConfig: RequestInit | undefined) =>
    authorizedFetch(url, { ...requestConfig, keepalive: true })
});

export function sendGraphQlBeacon(query: RequestDocument, variables?: Variables) {
  graphQlBeaconClient.request({ document: query, variables });
}

async function makeRestCallAndGetStream(url: string, requestConfig?: RequestInit) {
  const response = await authorizedFetch(url, requestConfig);
  return response.body;
}

export async function makePostCallAndGetStream(url: string, body: unknown, headers?: Headers) {
  return await makeRestCallAndGetStream(url, {
    method: 'POST',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}

export async function makeRawPostCall(url: string, body: unknown, headers?: Headers) {
  return await makeRawRestCall(url, {
    method: 'POST',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}
export async function makePostCall(url: string, body: unknown, headers?: Headers) {
  return await makeRestCall(url, {
    method: 'POST',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}
export async function makePutCall(url: string, body: unknown, headers?: Headers) {
  return await makeRestCall(url, {
    method: 'PUT',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}

export async function makeGetCall(url: string, headers?: Headers) {
  return await makeRestCall(url, {
    method: 'GET',
    headers
  });
}

export async function makeDeleteCall(url: string, body: unknown, headers?: Headers) {
  return await makeRestCall(url, {
    method: 'DELETE',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}

export async function makePostCallWithoutShareTokens(url: string, body: unknown, headers?: Headers) {
  return await makeRestCallWithoutShareTokens(url, {
    method: 'POST',
    body: body instanceof FormData ? (body as BodyInit) : JSON.stringify(body),
    headers
  });
}

function createCaller(graphQLClient: GraphQLClient) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return async function makeGraphQlCall<T = any, V = Variables>(
    query: RequestDocument,
    variables?: V,
    queryName?: string
  ) {
    let name = queryName;
    if (!queryName) {
      name = guessQueryName(query as string);
    }
    try {
      addBreadcrumb({
        type: SentryBreadcrumbType.gql,
        category: SentryBreadcrumb.GraphQl,
        message: name,
        level: 'info'
      });
      const data = await graphQLClient.request({ document: query, variables: variables as Variables });
      return data as T;
    } catch (error) {
      console.error(JSON.stringify(error, undefined, 2));
      throw error;
    }
  };
}
export const makeGraphQlCall = createCaller(graphQLClient);
export const makeGraphQlCallSubscription = createCaller(graphQLClientNew);
export const makeGraphQlCallWithoutShareTokens = createCaller(graphQLClientWithoutShareTokens);

export type ResponseFormat<T> = {
  success: boolean;
  message: string;
  code?: string | number;
  data: T;

  error?: string;
  trace?: string;
  stack?: string;
};

export type XHRResponseType<T> = Promise<ResponseFormat<T>>;

type XHRMethod<T, S> = (variables: S) => XHRResponseType<T>;

type ExtractXHRReturnType<C extends Callable> =
  ReturnType<C> extends Promise<{
    success: boolean;
    message: string;
    data: infer D;
  }>
    ? { message: string; data: D }
    : { message: string; data: never };

export type ExtractXHRResponseDataType<C extends Callable> =
  ReturnType<C> extends Promise<{
    message: string;
    data: infer D;
  }>
    ? D
    : never;

const handleXHRResponse = <T>(response: ResponseFormat<T>) => {
  if (response.success) {
    return { message: response.message, data: response.data };
  } else {
    const err = new ProphecyError(response.message || response.error, {
      cause: response.code?.toString()
    });
    err.stack = formatErrorTrace(response.trace || response.stack);
    throw err;
  }
};

export const handleXHRError = <T>(
  err: ProphecyError | ResponseFormat<T>,
  onError: (err: ProphecyError) => void = errorHandler
) => {
  if (isProphecyError(err)) {
    onError(err);
  } else {
    try {
      handleXHRResponse(err);
    } catch (e) {
      onError(e as ProphecyError);
    }
  }
};

export function xhrWrapper<C extends Callable>(callback: C) {
  return async function (...args: Parameters<C>): Promise<ExtractXHRReturnType<C>> {
    const response = await callback(...args);
    return handleXHRResponse(response) as ExtractXHRReturnType<C>;
  };
}

export const mockXHRResponse = <T>(data: T, message: string = '', success: boolean = true) => {
  return Promise.resolve({ success, message, data }) as XHRResponseType<T>;
};

export type CustomQueryOptions<T extends { onError?: Callable }> = Omit<T, 'onError'> & {
  onError?: (err: ProphecyError) => boolean | void;
  sentryTag?: string;
  showErrorToast?: boolean;
};

const getDefaultRestQueryOptions = <T extends { onError?: Callable }>(options?: CustomQueryOptions<T>) => ({
  ...((options || {}) as T),
  onError: (err: ProphecyError | ResponseFormat<unknown>) => {
    // in case of error response code (other than 200) xhrWrapper will get skipped
    //   so we check here again
    handleXHRError(err, (err: ProphecyError) => {
      let handleDefault = true;

      if (options?.onError) {
        handleDefault = Boolean(options.onError(err));
      }

      if (handleDefault) {
        errorHandler(err, {
          sentryTag: options?.sentryTag,
          showErrorToast: options?.showErrorToast ?? true
        });
      }
    });
  }
});

export const useRestQuery = <T = unknown>(
  key: QueryKey,
  queryFn: () => XHRResponseType<T>,
  queryOptions: CustomQueryOptions<UseQueryOptions<ExtractXHRReturnType<typeof queryFn>, ProphecyError>> = {}
) => {
  const queryClient = useQueryClient();
  queryOptions.sentryTag ||= castArray(key).join('_');
  const _queryOptions = getDefaultRestQueryOptions(queryOptions);

  const queryResponse = useReactQuery<ExtractXHRReturnType<typeof queryFn>, ProphecyError>(
    key,
    () => xhrWrapper(queryFn)(),
    _queryOptions
  );

  const invalidate = usePersistentCallback(() => {
    queryClient.invalidateQueries(key);
  });

  return { ...queryResponse, invalidate };
};

export const useRestMutation = <T = unknown, S = unknown>(
  queryFn: XHRMethod<T, S>,
  queryOptions: CustomQueryOptions<UseMutationOptions<ExtractXHRReturnType<typeof queryFn>, ProphecyError, S>> & {
    sentryTag: SentryTags | string;
  }
) => {
  const _queryOptions = getDefaultRestQueryOptions(queryOptions);
  return useReactMutation<ExtractXHRReturnType<typeof queryFn>, ProphecyError, S>(xhrWrapper(queryFn), _queryOptions);
};

export function guessQueryName(query: string) {
  let name = '';
  try {
    name = query.split('(').shift()?.trim().split(' ').pop() as string;
  } catch (error) {}
  return name;
}

export function getGqlDefaultOptions<S extends ReactQueryError, T extends { onError?: Callable }>(
  queryOptions: T | undefined,
  queryName: string
) {
  return {
    ...(queryOptions || ({} as T)),
    // Avoid re-render on tab focus refetch when selected props unchanged.
    notifyOnChangeProps:
      (queryOptions as { notifyOnChangeProps?: unknown })?.notifyOnChangeProps ?? DEFAULT_QUERY_NOTIFY_PROPS,
    onError: (errorResponse: S) => {
      let handleDefault = true;
      if (queryOptions?.onError) {
        handleDefault = Boolean(queryOptions.onError(errorResponse, queryName));
      }
      if (!handleDefault) return;

      let errorMessage = readGraphQlErrorMessage(errorResponse);
      captureException({
        exception: new ProphecyError(errorMessage),
        errorTags: { [SentryTags.ProphecyErrorGqlType]: queryName || GqlErrorTags.QueryError }
      });
      message.error({ content: errorMessage });
    }
  };
}
export function useLazyGraphQlQuery<TData = unknown, V extends Variables = Variables>(
  key: QueryKey,
  query: TadaDocumentNode<TData, V> | string
) {
  const queryClient = useQueryClient();
  const queryName = guessQueryName(query as string);

  const execute = usePersistentCallback(async (variables?: V) => {
    const queryKey = getQueryKeyWithVariables(key, variables);
    return queryClient.ensureQueryData<TData>({
      queryKey,
      queryFn: () => makeGraphQlCall<TData, V>(query, variables, queryName)
    });
  });

  return { execute };
}

const createGraphQlQueryHook = (graphQlCallFn: typeof makeGraphQlCall) => {
  return function <TData = unknown, V extends Variables = Variables, TQueryFnData = unknown, TError = ReactQueryError>(
    key: QueryKey,
    query: TadaDocumentNode<TData, V> | string,
    queryOptions: UseQueryOptions<TQueryFnData, TError, TData, QueryKey> = {},
    variables?: V
  ) {
    const queryClient = useQueryClient();
    const queryName = guessQueryName(query as string);
    const _queryOptions = getGqlDefaultOptions(queryOptions, queryName);
    const queryKey = getQueryKeyWithVariables(key, variables);

    const response = useReactQuery<TQueryFnData, TError, TData, QueryKey>(
      queryKey,
      () => {
        return graphQlCallFn(query, variables, queryName);
      },
      _queryOptions
    );

    const invalidate = usePersistentCallback(() => {
      queryClient.invalidateQueries(queryKey);
    });

    return { ...response, invalidate };
  };
};

export const useGraphQlQuery = createGraphQlQueryHook(makeGraphQlCall);
export const useGraphQlQueryWithoutShareTokens = createGraphQlQueryHook(makeGraphQlCallWithoutShareTokens);

// easier approach for graphQl hook
export function useGraphQlQueryFn<TData = unknown, V extends Variables = Variables>(
  key: QueryKey,
  queryFn: (input: V) => Promise<TData>,
  queryOptions: UseQueryOptions<TData, ReactQueryError, TData, QueryKey> & { queryName: string },
  variables: V
) {
  const queryKey = getQueryKeyWithVariables(key.concat(queryOptions.queryName), variables);
  const _queryOptions = getGqlDefaultOptions(queryOptions, queryOptions.queryName);
  return useReactQuery<TData, ReactQueryError, TData, QueryKey>(queryKey, () => queryFn(variables), _queryOptions);
}

export function useInfiniteGraphQlQuery<
  TData = unknown,
  V = unknown,
  TQueryFnData = Partial<V>,
  TError = unknown,
  TQueryKey extends QueryKey = QueryKey
>(
  key: TQueryKey,
  query: TadaDocumentNode<TData, V> | string,
  queryOptions: UseInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey> = {},
  variables?: V
) {
  const queryName = guessQueryName(query as string);
  const _queryOptions = getGqlDefaultOptions(queryOptions, queryName);

  return useReactInfiniteQuery<TQueryFnData, TError, TData, TQueryKey>(
    key,
    ({ pageParam }) => {
      return makeGraphQlCall(query, { ...variables, ...pageParam }, queryName);
    },
    _queryOptions
  );
}

export function readGraphQlErrorMessage(_errorResponse: unknown) {
  const __errorResponse = _errorResponse as NestedData;
  let errorMessage = '';
  if (__errorResponse.response) {
    const errorResponse = __errorResponse.response as ReactQueryError;
    errorMessage = errorResponse?.errors[0]?.message || '';
  } else if (__errorResponse.errors) {
    errorMessage = __errorResponse?.errors[0]?.message || '';
  } else if (__errorResponse.message) {
    errorMessage = __errorResponse.message;
  }
  return errorMessage;
}

function createGraphQlMutationHook(graphQlCallFn: typeof makeGraphQlCall) {
  return function <
    TData = unknown,
    TVariables extends Variables = Variables,
    TError = ReactQueryError,
    TContext = unknown
  >(
    query: TadaDocumentNode<TData, TVariables> | string,
    queryOptions?: UseQueryOptions<TData, TError, TData, QueryKey>
  ) {
    const queryName = guessQueryName(query as unknown as string);
    const _queryOptions = getGqlDefaultOptions(queryOptions, queryName);

    const queryFunction: MutationFunction<TData, TVariables> = (args) => {
      return graphQlCallFn(query, args, queryName);
    };
    return useReactMutation<TData, TError, TVariables, TContext>(
      queryFunction,
      _queryOptions as UseMutationOptions<TData, TError, TVariables, TContext>
    );
  };
}

export const useGraphQlMutation = createGraphQlMutationHook(makeGraphQlCall);
export const useGraphQlMutationWithoutShareTokens = createGraphQlMutationHook(makeGraphQlCallWithoutShareTokens);
export function useGraphQlQuerySubscription<
  TData = unknown,
  V extends Variables = Variables,
  TQueryFnData = unknown,
  TError = ReactQueryError
>(
  key: QueryKey,
  query: TadaDocumentNode<TData, V> | string,
  queryOptions: UseQueryOptions<TQueryFnData, TError, TData, QueryKey> = {},
  variables?: V,
  onProgress?: AsyncEventProgress
) {
  const queryName = guessQueryName(query as string);
  const _queryOptions = getGqlDefaultOptions(queryOptions, queryName);
  const fn: QueryFunction<TQueryFnData, QueryKey> = async (args) => {
    const { operation_id } = (await makeGraphQlCallSubscription(query, args, queryName)) as { operation_id: string };
    const response = await mdClient.subscribe({
      id: operation_id,
      onProgress: onProgress || noop
    });
    // delay, so onProgress can settle first
    await delay(250);
    return response as TQueryFnData;
  };
  return useReactQuery<TQueryFnData, TError, TData, QueryKey>(
    getQueryKeyWithVariables(key, variables),
    fn,
    _queryOptions
  );
}
export function useGraphQlMutationSubscription<
  TData = unknown,
  TVariables extends Variables = Variables,
  TError = ReactQueryError,
  TContext = unknown
>(
  query: TadaDocumentNode<TData, TVariables> | string,
  queryOptions?: UseQueryOptions<TData, TError, TData, QueryKey>,
  onProgress?: AsyncEventProgress
) {
  const queryName = guessQueryName(query as unknown as string);
  const _queryOptions = getGqlDefaultOptions(queryOptions, queryName);

  const queryFunction: MutationFunction<TData, TVariables> = async (args) => {
    const { operation_id } = (await makeGraphQlCallSubscription(query, args, queryName)) as { operation_id: string };
    const response = await mdClient.subscribe({
      id: operation_id,
      onProgress: onProgress || noop
    });
    // delay, so onProgress can settle first
    await delay(250);
    return response as TData;
  };
  return useReactMutation<TData, TError, TVariables, TContext>(
    queryFunction,
    _queryOptions as UseMutationOptions<TData, TError, TVariables, TContext>
  );
}

export function useGraphQlMutationFn<TData = unknown, TVariables extends Variables = Variables>(
  queryFn: MutationFunction<TData, TVariables>,
  queryOptions: Parameters<typeof useReactMutation>[2] & { queryName: string }
) {
  const _queryOptions = getGqlDefaultOptions(queryOptions, queryOptions.queryName);
  return useReactMutation<TData, ReactQueryError, TVariables>(
    queryFn,
    _queryOptions as UseMutationOptions<TData, ReactQueryError, TVariables>
  );
}

// hook to check after mutation if its dependent queries are refetching
export function useDependentDataFetching(mutationInProgress: boolean, ...queryKeys: QueryKey[]) {
  const [listenForFetch, setListenForFetch] = useState(false);

  const _fetchInProgress = useIsFetching({
    predicate: (query) => {
      return queryKeys.some((key) => partialMatchKey(query.queryKey, key));
    }
  });

  const fetchInProgress = _fetchInProgress > 0;

  useEffect(() => {
    if (mutationInProgress) {
      setListenForFetch(true);
    }
  }, [mutationInProgress]);

  useEffect(() => {
    /**
     * If fetch in progress settles, reset the listenForFetch.
     * There can be a case where fetch is happen from some previous invalidation, in which case
     * it might resolve while mutation is in progress, we need to ignore such cases
     */
    if (!fetchInProgress && !mutationInProgress) {
      setListenForFetch(false);
    }
    // Note: We just want to watch for fetchInProgress and not mutationInProgress, we just want to use the value of mutationInProgress at the time
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchInProgress]);

  // we only need to listen for fetches after the mutation is completed
  return listenForFetch && fetchInProgress && !mutationInProgress;
}

/**
 * couple of time we just want to get some data from the backend on demand basis, which is not required to be cached.
 * This hook lets calling any api call to retrive data without caching it.
 */
export function useFetch<TData, TVariables>(cb: MutationFunction<TData, TVariables>) {
  const { isLoading, data, error, mutateAsync: fetch } = useMutation(cb);

  return { data, isLoading, error, fetch };
}

export function useFetchStreamForString<TParams>(
  fetchFn: (params: TParams) => Promise<Response['body']>,
  onUpdate: (data: string, isFirstTime: boolean, isDone: boolean) => void
) {
  const decoder = useRef(new TextDecoder());
  const concatFn = useCallback((accumulatedData: string, chunk: string) => accumulatedData + chunk, []);
  const decoderFn = useCallback((value: Uint8Array) => decoder.current.decode(value, { stream: true }), []);
  return useFetchStream(fetchFn, onUpdate, concatFn, decoderFn);
}

/**
 * TODO: Add a abort controller to the fetch stream to cancel the request if the component is unmounted
 * This hook lets calling any api call to retrieve data without caching it.
 * This is useful for streaming data from the backend.
 */
export function useFetchStream<TData, TParams>(
  fetchFn: (params: TParams) => Promise<Response['body']>,
  onUpdate: (data: TData, isFirstTime: boolean, isDone: boolean) => void,
  concatFn: (accumulatedData: TData, chunk: TData) => TData,
  decoderFn: (value: Uint8Array) => TData
) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const data = useRef<TData | null>(null);

  const fetchStream = useCallback(
    async (params: TParams) => {
      setIsLoading(true);
      setError(null);
      try {
        const body = await fetchFn(params);
        if (!body) throw new Error('No response body');
        const reader = body.getReader();

        let accumulatedData: TData = decoderFn(new Uint8Array());

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            onUpdate(accumulatedData, false, true);
            break;
          }

          const isFirstTime = data.current === null;

          const chunk = decoderFn(value);
          accumulatedData = concatFn(accumulatedData, chunk);
          onUpdate(accumulatedData, isFirstTime, false);
          data.current = accumulatedData;
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFn, onUpdate, data, concatFn, decoderFn]
  );

  return { data, isLoading, error, fetchStream };
}

/**
 * Casts a value to an array. If the value is not an array, it will be wrapped in an array. If the value is undefined, it will return an empty array.
 * @param value - The value to cast to an array
 * @returns The value as an array
 */
export function castArray<T>(value: T | T[] | undefined): T[] {
  if (Array.isArray(value)) {
    return value;
  }

  return value ? [value] : (FROZEN_EMPTY_ARRAY as T[]);
}
